import { Avatar, AvatarInitials } from "@/components/Avatar";
import { GroupSelector } from "@/components/share/GroupSelector";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectSeparator,
    SelectTrigger,
    SelectValue,
} from "@/components/podkit/select/Select";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { Heading4 } from "@/components/podkit/typography/Headings";
import { Text } from "@/components/podkit/typography/Text";
import type { ShareableResource } from "@/contexts/ShareResourceContext";
import {
    useBatchUpdateRoleAssignments,
    useListGroups,
    useListRoleAssignments,
    type BatchRoleAssignmentOperation,
    type PlainGroup,
    type PlainRoleAssignment,
} from "@/queries/group-queries";
import { formatError } from "@/utils/errors";
import { ResourceType } from "gitpod-next-api/gitpod/v1/event_pb";
import { ResourceRole } from "gitpod-next-api/gitpod/v1/group_pb";
import { Trash2 } from "lucide-react";
import { useCallback, useMemo, type FC } from "react";

// Helper to get role label and description for project roles
const getProjectRoleInfo = (role: ResourceRole): { label: string; description: string } => {
    switch (role) {
        case ResourceRole.PROJECT_ADMIN:
            return {
                label: "Admin",
                description: "Full Project control including sharing and management.",
            };
        case ResourceRole.PROJECT_EDITOR:
            return {
                label: "Editor",
                description: "Can update, delete, and manage Environment classes and Secrets.",
            };
        case ResourceRole.PROJECT_USER:
            return {
                label: "User",
                description: "Basic Project access for viewing and using.",
            };
        default:
            return {
                label: "User",
                description: "Basic Project access for viewing and using.",
            };
    }
};

// Helper to get role label and description for runner roles
const getRunnerRoleInfo = (role: ResourceRole): { label: string; description: string } => {
    switch (role) {
        case ResourceRole.RUNNER_ADMIN:
            return {
                label: "Admin",
                description: "Full Runner control including configuration and management.",
            };
        case ResourceRole.RUNNER_USER:
            return {
                label: "User",
                description: "Can use the runner to create and manage environments.",
            };
        default:
            return {
                label: "User",
                description: "Can use the runner to create and manage environments.",
            };
    }
};

// Helper to get available roles for a resource type
const getAvailableRoles = (resourceType: ResourceType): ResourceRole[] => {
    switch (resourceType) {
        case ResourceType.PROJECT:
            return [ResourceRole.PROJECT_USER, ResourceRole.PROJECT_EDITOR, ResourceRole.PROJECT_ADMIN];
        case ResourceType.RUNNER:
            return [ResourceRole.RUNNER_USER, ResourceRole.RUNNER_ADMIN];
        default:
            return [];
    }
};

// Helper to get role info based on resource type
const getRoleInfo = (resourceType: ResourceType, role: ResourceRole): { label: string; description: string } => {
    switch (resourceType) {
        case ResourceType.PROJECT:
            return getProjectRoleInfo(role);
        case ResourceType.RUNNER:
            return getRunnerRoleInfo(role);
        default:
            return { label: role.toString(), description: "" };
    }
};

type Props = {
    resource: ShareableResource;
    onGroupsAdded?: (groupIds: string[]) => void;
    onGroupRemoved?: (groupId: string) => void;
};

export const GroupsWithAccessSection: FC<Props> = ({
    resource: { resourceType, resourceId },
    onGroupsAdded,
    onGroupRemoved,
}) => {
    const { toast } = useToast();
    const { data: groupsData } = useListGroups();
    const { data: assignmentsData } = useListRoleAssignments({ resourceType, resourceId });
    const batchUpdate = useBatchUpdateRoleAssignments();

    const availableRoles = useMemo(() => getAvailableRoles(resourceType), [resourceType]);
    const defaultRole = availableRoles[0] || ResourceRole.UNSPECIFIED;

    // Filter out system-managed groups (including org-members)
    // System-managed groups are handled separately in GeneralAccessSection
    const availableGroups = useMemo(() => {
        if (!groupsData?.groups) return [];
        return groupsData.groups.filter((g) => !g.systemManaged);
    }, [groupsData]);

    // Get role assignments for this resource
    const roleAssignments = useMemo(() => {
        if (!assignmentsData?.assignments) return [];
        return assignmentsData.assignments;
    }, [assignmentsData]);

    // Get groups that already have role assignments
    const assignedGroupIds = useMemo(() => {
        const ids = new Set(roleAssignments.map((a) => a.groupId));
        return ids;
    }, [roleAssignments]);

    // Get groups available for selection (not already assigned)
    const selectableGroups = useMemo(
        () => availableGroups.filter((g) => !assignedGroupIds.has(g.id)),
        [availableGroups, assignedGroupIds],
    );

    const handleAddGroups = useCallback(
        async (groupIds: string[]) => {
            try {
                const operations: BatchRoleAssignmentOperation[] = groupIds.map((groupId) => ({
                    type: "create" as const,
                    groupId,
                    role: defaultRole,
                }));

                await batchUpdate.mutateAsync({
                    resourceType,
                    resourceId,
                    operations,
                });

                toast({
                    title: "Group access updated",
                });

                // Notify parent about the change
                onGroupsAdded?.(groupIds);
            } catch (error) {
                toast({
                    title: "Failed to update group access",
                    description: formatError(error),
                });
            }
        },
        [defaultRole, resourceType, resourceId, batchUpdate, toast, onGroupsAdded],
    );

    const handleRoleChange = useCallback(
        async (groupId: string, value: string) => {
            try {
                const existing = roleAssignments.find((a) => a.groupId === groupId);
                if (!existing) return;

                // Handle deletion
                if (value === "delete") {
                    const operations: BatchRoleAssignmentOperation[] = [
                        {
                            type: "delete",
                            assignmentId: existing.id,
                        },
                    ];

                    await batchUpdate.mutateAsync({
                        resourceType,
                        resourceId,
                        operations,
                    });

                    toast({
                        title: "Group access removed",
                    });

                    // Notify parent about the removal
                    onGroupRemoved?.(groupId);
                    return;
                }

                // Handle role update
                const role = parseInt(value) as ResourceRole;
                const operations: BatchRoleAssignmentOperation[] = [
                    {
                        type: "update",
                        assignmentId: existing.id,
                        groupId,
                        role,
                    },
                ];

                await batchUpdate.mutateAsync({
                    resourceType,
                    resourceId,
                    operations,
                });

                toast({
                    title: "Group access updated",
                });
            } catch (error) {
                toast({
                    title: "Failed to update group access",
                    description: formatError(error),
                });
            }
        },
        [roleAssignments, resourceType, resourceId, batchUpdate, toast, onGroupRemoved],
    );

    // Get display list of groups with their roles, sorted by name
    const displayGroups = useMemo(() => {
        const groups: { group: PlainGroup; role: ResourceRole; assignment?: PlainRoleAssignment }[] = [];

        roleAssignments.forEach((assignment) => {
            const group = availableGroups.find((g) => g.id === assignment.groupId);
            if (!group) return;

            groups.push({
                group,
                role: assignment.resourceRole,
                assignment,
            });
        });

        // Sort by group name alphabetically
        return groups.sort((a, b) => a.group.name.localeCompare(b.group.name));
    }, [roleAssignments, availableGroups]);

    return (
        <div className="flex flex-col gap-2">
            <Heading4 className="font-medium">Groups with access</Heading4>

            <GroupSelector groups={selectableGroups} loading={!groupsData} onAdd={handleAddGroups} />

            {displayGroups.length === 0 ? (
                <div className="flex items-center justify-center rounded-lg border border-border-light bg-surface-02 px-6 py-8">
                    <Text className="text-sm text-content-secondary">No groups have access</Text>
                </div>
            ) : (
                <div className="flex flex-col gap-1">
                    {displayGroups.map(({ group, role }) => (
                        <div
                            key={group.id}
                            className="flex items-center gap-2 px-0 py-1"
                            data-testid={`group-assignment-${group.id}`}
                        >
                            <Avatar size={32} className="size-8 rounded-md">
                                <AvatarInitials name={group.name} size={32} />
                            </Avatar>
                            <div className="flex min-w-0 flex-1 flex-col">
                                <Text className="truncate text-base">{group.name}</Text>
                                <Text className="text-sm text-content-tertiary">
                                    {group.memberCount} <span>{group.memberCount === 1 ? "member" : "members"}</span>
                                </Text>
                            </div>
                            <Select
                                value={role.toString()}
                                onValueChange={(value) => handleRoleChange(group.id, value)}
                            >
                                <SelectTrigger className="w-32" loading={false} data-testid={`role-select-${group.id}`}>
                                    <SelectValue>
                                        <span className="text-base">{getRoleInfo(resourceType, role).label}</span>
                                    </SelectValue>
                                </SelectTrigger>
                                <SelectContent className="w-fit" align="end">
                                    {availableRoles.map((r) => {
                                        const roleInfo = getRoleInfo(resourceType, r);
                                        return (
                                            <SelectItem key={r} value={r.toString()}>
                                                <div className="mr-6 flex flex-col gap-0.5">
                                                    <span className="text-base">{roleInfo.label}</span>
                                                    <span className="truncate text-sm text-content-secondary">
                                                        {roleInfo.description}
                                                    </span>
                                                </div>
                                            </SelectItem>
                                        );
                                    })}
                                    <SelectSeparator />
                                    <SelectItem value="delete">
                                        <div className="flex items-center gap-2 text-content-destructive">
                                            <Trash2 className="size-4" />
                                            <span className="text-base">Remove access</span>
                                        </div>
                                    </SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};
